import { Link } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { SubmitHandler } from 'react-hook-form';
import { Link as LinkRouter, useNavigate } from 'react-router-dom';
import UserContext from '../../../context/userContext';
import GridWithToolbar from '../../grid/GridWithToolbar';
import { successToast } from '../../Toast';
import { ClientsApi, RolesApi } from './api';
import { ClientForm } from './components';
import {
	CLIENTS_TABLE_CLIENT_ID_LABEL,
	CLIENTS_TABLE_LEGACY_TENANT_LABEL,
	CLIENTS_TABLE_NOT_AVAILABLE_CELL,
	CLIENTS_TABLE_OPEN_CLIENT_FORM_BUTTON,
	CLIENTS_TABLE_TENANT_LABEL,
	CLIENTS_TABLE_TITLE,
	CLIENT_FORM__SUCCESS_CREATE_MESSAGE,
} from './data/constants';
import { Client, ClientRequest, UserRole } from './types';

interface ClientRole extends Client, UserRole {}

export default function ClientsGrid() {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { selectedUserRole: user } = React.useContext(UserContext);
	const CLIENT_KEY = ClientsApi.CLIENT_KEY + '-' + user.namespace;
	const ROLES_KEY = RolesApi.ROLES_KEY + '-' + user.namespace;
	const [
		isAddClientConfirmationModalOpen,
		setIsAddClientConfirmationModalOpen,
	] = React.useState(false);

	const { data: rolesData } = useQuery({
		queryKey: [ROLES_KEY],
		queryFn: RolesApi.getRoles,
	});

	const addClientMigration = useMutation(ClientsApi.addClient, {
		onSuccess: (data) => {
			// Add new user to the list
			const currClients = queryClient.getQueryData<Client[]>([CLIENT_KEY]);
			queryClient.setQueryData([CLIENT_KEY], [...currClients, data]);

			queryClient.invalidateQueries([CLIENT_KEY]);
			queryClient.setQueryData([[CLIENT_KEY], data.clientUuid], () => data);
			queryClient.setQueryData(
				[CLIENT_KEY, data.clientUuid, 'new-client-secret'],
				() => data.clientSecret,
			);

			successToast(CLIENT_FORM__SUCCESS_CREATE_MESSAGE);
			setIsAddClientConfirmationModalOpen(false);
			navigate(data.clientUuid);
		},
	});

	const {
		isRefetching,
		isLoading: clientsLoading,
		data: clients,
	} = useQuery({ queryKey: [CLIENT_KEY], queryFn: ClientsApi.getClients });

	const ActionButtons = (params: GridRenderCellParams) => (
		<Link
			underline='hover'
			component={LinkRouter}
			to={`/admin/clients/${params.row.clientUuid}`}
		>
			{params.value}
		</Link>
	);

	const handleAddClient: SubmitHandler<ClientRequest> = (values) => {
		addClientMigration.mutate(values);
	};

	const columns: GridColDef[] = [
		{
			field: 'clientId',
			headerName: CLIENTS_TABLE_CLIENT_ID_LABEL,
			width: 320,
			renderCell: ActionButtons,
		},
		{ field: 'tenant', headerName: CLIENTS_TABLE_TENANT_LABEL, width: 320 },
		{
			field: 'role',
			headerName: CLIENTS_TABLE_LEGACY_TENANT_LABEL,
			width: 320,
			flex: 1,
		},
	];

	return (
		<>
			<GridWithToolbar
				rowId='clientId'
				title={CLIENTS_TABLE_TITLE}
				isRefetching={isRefetching}
				key={clients?.length}
				data={clients?.map((client, index) => {
					const roleMatch = rolesData?.find(
						(role) => role?.role === client?.role,
					);
					if (roleMatch) {
						const role: ClientRole = {
							...client,
							...roleMatch,
						};
						return {
							role:
								role?.namespaceRoleTypeDisplayName ||
								role?.accountTypeDisplayName === ''
									? CLIENTS_TABLE_NOT_AVAILABLE_CELL
									: role?.accountTypeDisplayName,
							clientId: role?.clientId,
							clientUuid: role?.clientUuid,
							tenant: role?.tenantHostname || CLIENTS_TABLE_NOT_AVAILABLE_CELL,
						};
					} else {
						return {
							role: CLIENTS_TABLE_NOT_AVAILABLE_CELL,
							clientId: client?.clientId,
							clientUuid: client?.clientUuid,
						};
					}
				})}
				columns={columns}
				loading={clientsLoading}
				buttonText={CLIENTS_TABLE_OPEN_CLIENT_FORM_BUTTON}
				onButtonClick={() => setIsAddClientConfirmationModalOpen(true)}
			/>
			<Dialog
				open={isAddClientConfirmationModalOpen}
				onClose={() => setIsAddClientConfirmationModalOpen(false)}
			>
				<DialogTitle>{CLIENTS_TABLE_OPEN_CLIENT_FORM_BUTTON}</DialogTitle>
				<DialogContent sx={{ width: 600 }}>
					<ClientForm
						initialValues={{
							clientId: '',
							role: '',
							tenant: '',
						}}
						isLoading={addClientMigration.isLoading}
						handleSubmit={handleAddClient}
						handleClose={() => setIsAddClientConfirmationModalOpen(false)}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
}
